<template>
  <div
    ref="$el"
    :class="wrapperClass"
    @click="e => handleClick(e)"
    @mousedown="e => handleMousedown(e)"
    @mouseenter="e => handleMouseenter(e)"
    @mouseleave="e => handleMouseleave(e)"
    @mouseup="e => handleMouseup(e)"
  >
    <div :class="cmpClass">
      <slot v-bind="props">
        <div
          v-if="withAvatar"
          class="d-meta-avatar"
          :style="{
            gridColumn: (withTitle || withDescription || withActions) ? '1 / 2' : '1 / -1',
            alignSelf: avatarAlign,
          }"
        >
          <slot name="avatar">
            <Avatar v-bind="avatarProps" />
          </slot>
        </div>
        <div
          v-if="withTitle"
          class="d-meta-title"
          :style="{
            gridRow: withDescription ? '1 / 2' : '1 / -1',
            ...mainStyle,
          }"
        >
          <slot name="title">
            <Text
              class="d-meta-title-default"
              color="text-title"
              bold
              ellipsis
              tooltip
            >
              {{ title }}
            </Text>
          </slot>
        </div>
        <div
          v-if="withDescription"
          class="d-meta-description"
          :style="{
            gridRow: withTitle ? '2 / 3' : '1 / -1',
            ...mainStyle,
          }"
        >
          <slot name="description">
            <Text class="d-meta-description-default">{{ description }}</Text>
          </slot>
        </div>
        <div
          v-if="withActions"
          class="d-meta-actions"
          :style="{
            gridColumn: (withTitle || withDescription)
              ? '3 / -1'
              : withAvatar
                ? '2 / -1'
                : '1 / -1',
            alignSelf: actionsAlign,
          }"
        >
          <slot name="actions">
            <Space :size="0">
              <template v-if="actions.length < 4">
                <Button
                  v-for="(action, i) of actions"
                  :key="`${i}-${action.name}`"
                  class="d-meta-action-default"
                  size="small"
                  type="light"
                  @click="action.onClick"
                >
                  {{ action.name }}
                </Button>
              </template>
              <template v-else>
                <Button
                  v-for="(action, i) of actions.slice(0, 2)"
                  :key="`${i}-${action.name}`"
                  class="d-meta-action-default"
                  size="small"
                  type="light"
                  @click="action.onClick"
                >
                  {{ action.name }}
                </Button>
                <Popover
                  class="d-meta-action-popover"
                  placement="bottom-end"
                >
                  <Button
                    class="d-meta-action-default"
                    size="small"
                    type="light"
                    :icon="{ icon: Down, size: 'default', color: 'text-description' }"
                    icon-position="right"
                  >
                    {{ '更多' }}
                  </Button>
                  <template #content>
                    <Space
                      class="--space-p-small"
                      direction="vertical"
                      align="start"
                      size="small"
                    >
                      <Text
                        v-for="(action, i) of actions.slice(2)"
                        :key="`${i}-${action.name}`"
                        class="d-meta-action-extra"
                        color="text-title"
                        @click="action.onClick"
                      >
                        {{ action.name }}
                      </Text>
                    </Space>
                  </template>
                </Popover>
              </template>
            </Space>
          </slot>
        </div>
      </slot>
    </div>
  </div>
</template>
<script setup lang="ts">
  import {
    ref, reactive, computed, watch, watchEffect, useSlots, withDefaults,
  } from 'vue'
  import { AvatarContent, Avatar } from '../Avatar'
  import { Text } from '../Text'
  import { Button } from '../Button'
  import { Popover } from '../Popover'
  import { Space } from '../Space'
  import { Down } from '../../icons'
  import { MetaAction } from './interface'
  import { MetaSpace, Align } from '../../enums'
  import { MouseState } from '../../states'

  const props = withDefaults(
    defineProps<{
      space?: MetaSpace
      avatar?: string | Partial<AvatarContent>
      avatarAlign?: Align
      title?: string
      description?: string
      actions?: MetaAction[]
      actionsAlign?: Align
      extra?: Record<string, any>
    }>(),
    {
      space: 'default',
      avatar: undefined,
      avatarAlign: 'center',
      title: undefined,
      description: undefined,
      actions: undefined,
      actionsAlign: 'center',
      extra: undefined,
    },
  )

  const emits = defineEmits<{(e: 'click', event: MouseEvent): void; (e: 'mousedown', event: MouseEvent): void; (e: 'mouseenter', event: MouseEvent): void; (e: 'mouseleave', event: MouseEvent): void; (e: 'mouseup', event: MouseEvent): void; (e: 'state-change', state: MouseState): void}>()

  const state = reactive<MouseState>({
    pressing: false,
    hover: false,
  })

  watch(
    state,
    v => emits('state-change', v),
  )

  const avatarProps = computed(
    () => (
      typeof props.avatar === 'string'
        ? {
          src: props.avatar,
        }
        : props.avatar
    ),
  )

  const slots = useSlots()

  const withAvatar = computed(
    () => !!slots.avatar || props.avatar !== undefined,
  )

  const withTitle = computed(
    () => !!slots.title || props.title !== undefined,
  )

  const withDescription = computed(
    () => !!slots.description || props.description !== undefined,
  )

  const withActions = computed(
    () => !!slots.actions || props.actions?.length > 0,
  )

  const wrapperClass = computed(
    () => [
      'd-meta-wrapper',
      `d-meta-wrapper-space-${props.space}`,
      state.pressing && 'pressing',
    ],
  )

  const cmpClass = computed(
    () => [
      'd-meta',
      `d-meta-space-${props.space}`,
      slots.default && 'custom',
    ],
  )

  const mainStyle = computed(
    () => ({
      // eslint-disable-next-line no-nested-ternary
      gridColumn: withAvatar.value
        ? withActions.value
          ? '2 / 3'
          : '2 / -1'
        : withActions.value
          ? '1 / 3'
          : '1 / -1',
    }),
  )

  function handleClick(e: MouseEvent) {
    emits('click', e)
  }

  function handleMousedown(e: MouseEvent) {
    emits('mousedown', e)
    state.pressing = true
  }

  function handleMouseenter(e: MouseEvent) {
    emits('mouseenter', e)
    state.hover = true
  }

  function handleMouseleave(e: MouseEvent) {
    emits('mouseleave', e)
    state.pressing = false
    state.hover = false
  }

  function handleMouseup(e: MouseEvent) {
    emits('mouseup', e)
    state.pressing = false
  }

  const $el = ref<HTMLElement>()

  const exposed = reactive({
    $el: $el.value,
  })

  watchEffect(
    () => {
      exposed.$el = $el.value
    },
  )

  defineExpose(exposed)
</script>
